import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { Cookies } from 'react-cookie-consent';

// Services
import { uniqueGroupSessionHistory } from "../Services/ApiServices/chatServices";
import { getRandomizedPrompts, isTwoMonthsFromNow } from "../Services/CommonServices/helper";
import {
  apiError,
  conversationList,
  isStream,
  historyListArray,
  searchBoxVisible
} from "../Services/Redux/Reducers/chatSlice";
import {
  loader,
  isOnBoardHeader,
  fromCloseHistory,
  onHistoryClick
} from "../Services/Redux/Reducers/commonSlice";
import {
  appFeedBackModal,
  appLevelFeedback
} from "../Services/Redux/Reducers/feedBackSlice";
import { userPrompt } from "../Services/Redux/Reducers/userSlice";

// Components
import ApplicationFeedback from "../CommonComponents/ApplicationFeedback";
import OnboardCarousel from "./OnboardingCarousel/OnboardCarousel";
import HistoryList from "../CommonComponents/ArchiveModal";
import PromptsCarousel from "./PromptCarousel/PromptsCarousel";
import OnboardHeader from "../CommonComponents/OnboardHeader";
import AutoplayVideoPlayer from "../CommonComponents/AutoplayVideoPlayer";

// Configuration for basic video fallback
const VIDEO_CONFIG = {
  staticBackgroundPath: './static/assets/static-bg.jpg'
  // staticBackgroundPath: './static/assets/OnboardingImages/onboardingBackground.png'
};

function Useronboard(props) {
  // ---------- Component State ----------
  // Input handling state
  const [input, setInput] = useState("");
  const [isPlaceHolderVisible, setIsPlaceHolderVisible] = useState(true);
  const [isSendButtonDisabled, setIsSendButtonDisabled] = useState(true);
  const [isKeyBoardOpen, setIsKeyBoardOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  // UI State
  const [blurBackground, setBlurBackground] = useState(false);
  const [rightPadding, setRightPadding] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Loading and Modal States
  const [isLoading, setIsLoading] = useState(true);
  const [onboardLoading, setOnboardLoading] = useState(false);
  const [modalDeleteState, setModalDeleteState] = useState(false);

  // Carousel and History States
  const [showMobileCarousel, setShowMobileCarousel] = useState(false);
  const [promptsData, setPromptsData] = useState([]);
  const [hideHistory, setHideHistory] = useState(false);
  const [isBackFromArchive, setIsBackFromArchive] = useState(false);
  const [showTutorialData, setShowTutorialData] = useState(false);
  const [isPaused, setIsPaused] = useState(false);


  // ---------- Refs ----------
  const inputRef = useRef(null);
  const submitButtonRef = useRef(null);
  const isMounted = useRef(true);
  const prevIndexRef = useRef(0);
  const onboardPlayerRef = useRef(null);

  // ---------- Routing ----------
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // ---------- Redux State ----------
  const dispatch = useDispatch();

  // Chat State
  const carouselData = useSelector((state) => state.chat.historyListArray);

  // Common State
  const isTutorialClicked = useSelector((state) => state.common.isTutorial);
  const showOnboardHeader = useSelector((state) => state.common.isOnBoardHeader);
  const historyClick = useSelector((state) => state.common.onHistoryClick);

  // Feedback State
  const modalFlag = useSelector((state) => state.feedBack.appFeedBackModal);
  const summaryData = useSelector((state) => state.feedBack.appLevelFeedback);

  // ---------- User Context ----------
  // const name = Cookies?.get('ar_user_email') || "";
  const customerId = Cookies?.get('ar_user_Id') || "";
  const bucketUrl = Cookies.get('bucketUrl') || props.bucketUrl;
  const cardFolder = Cookies.get('cardFolder') || props.cardFolder;

  useEffect(() => {
    if (Cookies.get('bucketUrl') || props.bucketUrl) {
      fetchPrompts(Cookies.get('bucketUrl') || props.bucketUrl);
    }
  }, [Cookies, props.bucketUrl]);

  // Effect to fetch prompts when URL is available

  const fetchPrompts = async (bucketUrl) => {
    const response = await fetch(`${bucketUrl}Dynamic_Prompts/landing_prompts.json`)
    const result = await getRandomizedPrompts(response, 2);
    setPromptsData(result);
  }

  useEffect(() => {
    if (isTutorialClicked)
      setShowTutorialData(true)
  }, [isTutorialClicked]);

  useEffect(() => {
    dispatch(conversationList([]));
    dispatch(userPrompt(''));
    dispatch(isStream(false));
    dispatch(loader(false));
    const handleResize = () => {
      const mobileView = window.innerWidth <= 576;
      const focused = window.innerHeight < window.screen.height * 0.7;
      setIsKeyBoardOpen(focused);
      setIsMobile(mobileView); // Adjust the width as needed
      if (!mobileView) {
        // Reset to initial state when switching to desktop view
        // setShowLogo(true);
        setShowMobileCarousel(false);
        setBlurBackground(false);
        dispatch(searchBoxVisible(true));
      }
    };

    handleResize(); // Check initial screen size
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSkip = () => {
    dispatch(searchBoxVisible(true));
  }

  const handleGetStartedClick = () => {
    setBlurBackground(false);
    setTimeout(() => { setShowMobileCarousel(false); }, 500);
    setIsBackFromArchive(true);
  };

  // Event handlers
  const handleFocus = (e) => {
    setIsPlaceHolderVisible(false);
    setIsFocused(true);
    setIsSendButtonDisabled(false);
    setIsPaused(true);
  }

  const handleInputBlur = () => {
    setIsFocused(false);
    setIsSendButtonDisabled(input.trim() === '');
    setIsPaused(false);
  }

  // Data fetching
  const storeMessage = async () => {
    if (process.env.REACT_APP_APPLICATION_VERSION === "Microsoft") {
      return;
    }
    try {
      uniqueGroupSessionHistory(customerId, (response) => {
        dispatch(historyListArray(response.data));
        dispatch(appLevelFeedback(response.data));
        setIsLoading(false);
      }, (err) => {
        dispatch(apiError(err.message));
        dispatch(loader(false));
      });
    } catch (err) {
      console.log('Error Message Occurred in Group Session history API call', err.message);
    }
  }

  // Effects
  useEffect(() => {
    // App feedback modal
    if (summaryData?.length >= 1) {
      const shouldShowFeedback = summaryData.feedBack_Length === 0 ||
        isTwoMonthsFromNow(summaryData?.feedBackData?.[0]?.requestDateTime);

      if (shouldShowFeedback) {
        dispatch(appFeedBackModal(true));
      }
    }
  }, [summaryData, dispatch]);

  useEffect(() => {
    // Clear session and handle onboarding header
    sessionStorage.removeItem("persist_session");
    dispatch(isOnBoardHeader(true));
    
    if (customerId) {
      storeMessage();
    }
  }, [modalDeleteState, customerId, dispatch]);

  // Handle Enter key press 
  const handleKeyPress = (event) => {
    const isEnterKey = event.key === 'Enter' || event.code === 'Enter' || event.keyCode === 13 || event.which === 13;
    if (isEnterKey && !event.shiftKey) {
      event.preventDefault();
      setIsKeyBoardOpen(false);
      if (!isSendButtonDisabled && input.trim() !== '') {
        onHandAnimation();
        dispatch(isStream(true));
      }
      submitButtonRef.current.focus(); // Set focus to the submit button
    }
  };

  const onHandleInputChange = (e) => {
    const inputValue = e.target.value
    setInput(inputValue)
    setIsSendButtonDisabled(inputValue.trim() === '');
  }

  const onHandAnimation = () => {
    dispatch(isStream(true))
    dispatch(conversationList([]));
    setOnboardLoading(true);
    dispatch(loader(false))
    dispatch(userPrompt(input));
    setRightPadding(true);
    setTimeout(() => {
      navigate("/chat");
    }, 2000)
  }

  const handlePrepromptClick = (prepromptText) => {
    dispatch(isStream(true))
    dispatch(conversationList([]));
    dispatch(loader(false))
    dispatch(userPrompt(prepromptText));
    navigate("/chat");
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setIsPlaceHolderVisible(true);
        // startMsgAnimation();
        setIsPaused(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);


  const handleCaroselAnimate = (ques) => {
    setInput(ques);
    setIsPlaceHolderVisible(false);
    setIsSendButtonDisabled(false);
    dispatch(loader(false))
    const element = document.getElementById("para");
    if (element) {
      element.focus();
      // Force focus for Android devices
      setTimeout(() => {
        element.focus();
      }, 100);
    }
  }

  //   useEffect(() => {
  //     setTimeout(() => {
  //       if (document.getElementById("next")) {
  //         document.getElementById("next").click()
  //       }
  //     }, 3000);
  //   }, [showCarousel])

  const checkHistoryParam = () => {
    return searchParams.get('history') === 'active';
  };

  // Helper function to safely get bootstrap and create Offcanvas
  const getOffcanvas = (elementId) => {
    if (checkHistoryParam()) {
      const element = document.getElementById(elementId);
      if (!element) return null;

      // Safely access bootstrap from window object
      if (window.bootstrap && window.bootstrap.Offcanvas) {
        return new window.bootstrap.Offcanvas(element);
      }
      return null;
    }
  };

  const openHistoryModal = () => {
    dispatch(isOnBoardHeader(false));
    dispatch(onHistoryClick(true));
    setSearchParams({ ...Object.fromEntries(searchParams), history: 'active' });
    const el = document.getElementById("historyCanvas");
    if (el) {
      el.scrollIntoView({
        behavior: 'instant'
      });
    }
  };

  const closeHistoryModal = () => {
    dispatch(onHistoryClick(false));
    dispatch(isOnBoardHeader(true));
    setHideHistory(true);
    searchParams.delete('history');
    setSearchParams(searchParams);
    dispatch(fromCloseHistory(true))
  };

  // Enhanced useEffect to handle URL parameter changes and browser navigation
  useEffect(() => {
    const hasHistoryParam = searchParams.get('history') === 'active';

    if (!hasHistoryParam) {
      // If history parameter is not in URL, ensure modal is closed
      const offcanvasElement = document.getElementById('offcanvasRight');
      if (offcanvasElement && window.bootstrap && window.bootstrap.Offcanvas) {
        const bsOffcanvas = window.bootstrap.Offcanvas.getInstance(offcanvasElement);
        if (bsOffcanvas) {
          bsOffcanvas.hide();
          dispatch(isOnBoardHeader(true));
        }
      }
    }
  }, [searchParams, location.search]);

  return (
    <div>
      <AutoplayVideoPlayer
        url={`${bucketUrl}Header_Background_Images/askralph_ambient_bg_launch.mp4`}
        fallbackImage={VIDEO_CONFIG.staticBackgroundPath}
        className="onboard-video"
      />
      {showOnboardHeader &&
        <OnboardHeader carouselData={carouselData} openHistoryModal={openHistoryModal} />
      }
      {/* <div className={`modalContainer container container-sm container-xs container-md container-xl container-xxl container-fluid useronboard  mx-auto py-5  img-fluid px-0 ${animateState ? 'backgroundOut' : ''}${(blurBackground || isTutorial) ? 'blur' : ''}`}> */}
      <div className={`modalContainer container-fluid useronboard px-0 `}>
        {/* <div className={`${isBackFromArchive ? 'animateOnboardCarousel' : ""}`}> */}
        <div className="align-items-center justify-content-center onboard-elements-group">
          {/* <div className={`logo px-0 ${showLogo ? 'fade-in-logo' : 'fade-out-logo'} ${isKeyBoardOpen ? 'focused' : ''}`}> */}
          <div className={`logo fadein-logo px-0 ${isKeyBoardOpen ? 'focused' : ''}`}>
            <img
              src="./static/assets/AskRalphLogo_updated.svg"
              className="img-fluid logo-img"
              width="290px"
              height="auto"
              alt="no img"
            />
          </div>
          <>
            {
              process.env.REACT_APP_APPLICATION_VERSION !== "Microsoft" ?
                <>
                  <div className={`chatbox box-msg fade-in-elements d-flex justify-content-center align-items-center ${isKeyBoardOpen ? 'focused' : ''}`}>
                    <div className="gradient-border"></div>
                    <input value={input} ref={inputRef} className="form-control msg" type="text" id="para"
                      // autoFocus
                      enterKeyHint="send"
                      disabled={customerId === "" || customerId === null || customerId === undefined}
                      onFocus={(e) => handleFocus(e)}
                      onBlur={() => handleInputBlur}
                      onChange={(e) => onHandleInputChange(e)}
                      onKeyDown={(e) => {
                        if (input && e.code === "Enter") {
                          e.preventDefault()
                          onHandAnimation()
                          dispatch(isStream(true))
                          handleKeyPress(e);
                        }
                      }}
                    />

                    {(isPlaceHolderVisible && input === "") &&
                      <div className="message-container col-11" onClick={() => { document.getElementById("para").focus() }}>
                        <div className="placeholder_text">Ask a question...</div>
                      </div>
                    }

                    <button
                      ref={submitButtonRef}
                      name="submitMessage"
                      onClick={() => { onHandAnimation() }}
                      className={`arrow-dist send-arrow-position ${isSendButtonDisabled ? "disabled" : "enabled"} ${rightPadding ? 'sprkRght' : ''} `}
                      disabled={!input || (isFocused || input.trim() === '') && isSendButtonDisabled}
                    >
                      {!onboardLoading ? (
                        <img
                          src="/static/assets/bluearrow.svg"
                          height="30px"
                          width="30px"
                          alt="arrow-img"
                          className="arrow_long"
                        />

                      ) : (
                        <img
                          src="/static/assets/inputSparkle.gif"
                          alt="dot"
                          height="30px"
                          width="30px"
                          className="arrow_long round-img"
                        />
                      )}
                    </button>
                  </div>
                </>
                :
                <>
                  {/* <div className={`chatbox box-msg d-flex justify-content-center align-items-center ${!isBackFromArchive && 'animateSearch'}  ${animateState ? 'animateSearchOut' : ''} ${isKeyBoardOpen ? 'focused' : ''}`}> */}
                  <div className={`chatbox box-msg fade-in-elements d-flex justify-content-center align-items-center  ${isKeyBoardOpen ? 'focused' : ''}`}>
                    <div className="gradient-border"></div>
                    <input value={input} ref={inputRef} className="form-control msg" type="text" id="para"
                      // autoFocus
                      disabled={customerId === "" || customerId === null || customerId === undefined}
                      onFocus={handleFocus}
                      onBlur={handleInputBlur}
                      onChange={(e) => onHandleInputChange(e)}
                      onKeyDown={(e) => {
                        if (input && e.code === "Enter") {
                          e.preventDefault()
                          onHandAnimation()
                          dispatch(isStream(true))
                        }
                      }}
                    />

                    {/* Animated Placeholder Messages - Microsoft Version */}
                    {isPlaceHolderVisible && !input &&
                      <div className="message-container col-11" onClick={() => { document.getElementById("para").focus() }}>
                        <div className="placeholder_text">Ask a question...</div>
                      </div>
                    }

                    {!onboardLoading ? (
                      <button
                        name="submitMessage"
                        onClick={() => { onHandAnimation() }}
                        className={`arrow-dist send-arrow-position ${isSendButtonDisabled ? "disabled" : "enabled"} ${rightPadding ? 'sprkRght' : ''} `}
                        disabled={!input || (isFocused || input.trim() === '') && isSendButtonDisabled}
                      >
                        <img
                          src="/static/assets/bluearrow.svg"
                          height="36px"
                          width="36px"
                          alt="arrow-img"
                          className="arrow_long"
                        />
                      </button>
                    ) : (
                      <div className="onboard-loader" role="status"></div>
                    )}
                  </div>
                </>
            }
          </>

          <div className="preprompts_container d-flex fade-in-elements col-lg-9 col-xl-8 mx-lg-auto">
            {promptsData.map((item, index) => (
              <button
                name={"preprompts"+(index+1)}
                key={index}
                className="preprompts_box"
                onClick={() => handlePrepromptClick(item)}
                style={{
                  flexShrink: 0,
                  marginLeft: index === 0 ? '20px' : '0',
                  marginRight: index === promptsData.length - 1 ? '20px' : '0'
                }}
              >
                <span className="preprompts_text">{item}</span>
              </button>
            ))}
          </div>

          <div className="mx-0">
            <div className="container-fluid userOnboardContainer fade-in-elements col-12 col-sm-10 col-xl-9 px-0 px-xl-4 px-xxl-5">
              <div>
                <PromptsCarousel
                  handleCaroselAnimate={(q) => handleCaroselAnimate(q)}
                  isBackFromArchive={isBackFromArchive}
                  bucketUrl={bucketUrl}
                  cardFolder={cardFolder}
                  isKeyboardOpen={isKeyBoardOpen}
                />
              </div>
            </div>
          </div>
        </div>
        {process.env.REACT_APP_APPLICATION_VERSION !== "Microsoft" && modalFlag &&
          <div> <ApplicationFeedback /> </div>}
        <div className={`offcanvas offcanvas-bottom ${historyClick ? 'show' : hideHistory ? 'show hiding' : ''}`} tabIndex="-1" id="offcanvasRight" aria-labelledby="offcanvasRightLabel">
          <div className="offcanvas-header d-flex justify-content-end"
            style={{ position: "fixed", bottom: "40px", left: "50%", transform: "translateX(-50%)", zIndex: "1000" }}>
            <button
              name="closeButton"
              type="button"
              className=""
              onClick={closeHistoryModal}
              data-bs-dismiss="offcanvas"
              style={{
                backgroundColor: "#e1e1dc",
                borderRadius: "50%",
                padding: "0",
                width: "40px",
                height: "40px",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <img src="/static/assets/close.svg" alt="close" height="16px" width="16px" />
            </button>
          </div>
          <div className="offcanvas-body">
            <HistoryList data={carouselData} setModalDeleteState={setModalDeleteState} />
          </div>
        </div>
      </div>
      <OnboardCarousel handleGetStartedClick={handleGetStartedClick} handleSkip={handleSkip} showMobileCarousel={showMobileCarousel} isLoading={isLoading} carouselData={carouselData} showTutorialData={showTutorialData} setShowTutorialData={setShowTutorialData} blurBackground={blurBackground} bucketUrl={bucketUrl} cardFolder={cardFolder} />

    </div>
  );
}

export default Useronboard;